import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {CreateWorkforceProfileModal} from './CreateWorkforceProfileModal';
import {WorkforceProfileRow} from './WorkforceProfileRow';
import {WorkforceProfilesListFilterBanner} from './WorkforceProfilesListFilterBanner';
import {BasicTable} from '../../../../../components/BasicTable/BasicTable';
import {BasicTablePaginationFooter} from '../../../../../components/BasicTable/BasicTablePaginationFooter';
import {createBasicTableColumn} from '../../../../../components/BasicTable/createBasicTableColumn';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {Loading} from '../../../../../components/Loading/Loading';
import {CompanyContext} from '../../../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {useQueryPagination, PaginationResponse} from '../../../../../hooks/useQueryPagination';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {GetRoutes} from '../../../../../lib/api/GET/GetRoutes';
import {AppConfig} from '../../../../../lib/AppConfig';
import {ModuleTypes} from '../../../../../types/CompanyProductConfiguration/CompanyModule';
import {isCompanyModuleActive} from '../../../../../utils/companyModuleUtils/isCompanyModuleActive';
import {useWorkforceProfilesListFilter} from '../../hooks/useWorkforceProfilesListFilter';
import {useWorkforceProfilesStatusPolling} from '../../hooks/useWorkforceProfilesStatusPolling';
import {
  mapCompanyWorkforceProfilesResponse,
  WorkforceProfilesPaginatedResponse,
} from '../../serializers/WorkforceProfileSerializers';
import {CompanyWorkforceProfile, WorkforceProfileTableRow} from '../../types/WorkforceProfileTypes';
import {flattenWorkforceDepartmentOptions} from '../../utils/WorkforceProfiles/flattenWorkforceDepartmentOptions';
import {getWorkforceDepartmentDisplayName} from '../../utils/WorkforceProfiles/getWorkforceDepartmentDisplayName';

const mapResponse = (raw: unknown): PaginationResponse<CompanyWorkforceProfile, Record<string, never>> => {
  const result: WorkforceProfilesPaginatedResponse = mapCompanyWorkforceProfilesResponse(raw);
  return {
    data: result.data,
    pagination: result.pagination,
  };
};

export interface Props {
  createModalOpen: boolean;
  onCreateModalClose: () => void;
}

export const CompanyWorkforceProfilesTable = ({createModalOpen, onCreateModalClose}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {company, departments, productConfiguration} = useContext(CompanyContext);

  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage'});
  const {t: tShared} = useTranslation();

  const companyId = company.data?.resourceId ?? '';
  const isWorkforceModuleActive = isCompanyModuleActive(
    ModuleTypes.WorkforceModule,
    productConfiguration.data?.companyModules
  );

  const departmentOptions = useMemo(
    () => flattenWorkforceDepartmentOptions(departments.data ?? [], currentLanguage),
    [currentLanguage, departments.data]
  );

  const departmentLabelById = useMemo(
    () => new Map(departmentOptions.map((option) => [option.resourceId, option.label])),
    [departmentOptions]
  );

  const {
    data: workforceProfiles,
    pagination,
    isLoading: isProfilesLoading,
    error: profilesError,
    getNext,
    getPrevious,
    hasNext,
    hasPrevious,
    refreshData,
  } = useQueryPagination<CompanyWorkforceProfile, Record<string, never>>({
    url: GetRoutes.companyWorkforceProfiles(companyId),
    entity: 'companyWorkforceProfiles',
    perPage: AppConfig.PAGINATION_PER_PAGE ?? 25,
    enabled: isWorkforceModuleActive && !!companyId,
    mapResponse,
  });

  const {mergedProfiles} = useWorkforceProfilesStatusPolling({
    companyId,
    profiles: workforceProfiles,
  });

  const {
    filteredProfiles,
    hasListFilters,
    filterCount,
    matchedCount,
    clearFilter,
    isFetchingFilteredPages,
  } = useWorkforceProfilesListFilter(mergedProfiles, {
    hasNext,
    isLoading: isProfilesLoading,
    getNext,
  });

  const [page, setPage] = useState(1);

  const handlePageChange = useCallback(
    (targetPage: number) => {
      if (pagination && targetPage > page) {
        getNext();
      } else if (pagination && targetPage < page) {
        getPrevious();
      }
      setPage(targetPage);
    },
    [getNext, getPrevious, page, pagination]
  );

  useEffect(() => {
    if (pagination?.page) {
      setPage(pagination.page);
    }
  }, [pagination?.page]);

  useEffect(() => {
    if (profilesError) {
      dispatchMessage(tShared('shared.fetchingData.error'), 'error');
    }
  }, [dispatchMessage, profilesError, tShared]);

  const columns = useMemo(
    () => [
      createBasicTableColumn<WorkforceProfileTableRow>('resourceId', t('list.table.profileId')),
      createBasicTableColumn('departmentLabel', t('list.table.department')),
      createBasicTableColumn('seniority', t('list.table.seniority')),
      createBasicTableColumn('targetProfileTitle', t('list.table.targetProfile')),
      createBasicTableColumn('alignment', t('list.table.overallScore')),
      createBasicTableColumn('coreCompetenciesScore', t('list.table.coreCompetencies')),
      createBasicTableColumn('hardSkillsScore', t('list.table.hardSkills')),
      createBasicTableColumn('softSkillsScore', t('list.table.softSkills')),
      createBasicTableColumn('responsibilitiesScore', t('list.table.responsibilities')),
      createBasicTableColumn('culturalFitScore', t('list.table.culturalFit')),
      createBasicTableColumn('createdAt', t('list.table.created')),
      createBasicTableColumn('actions', '', {align: 'right'}),
    ],
    [t]
  );

  const rowData = useMemo<WorkforceProfileTableRow[]>(
    () =>
      filteredProfiles.map((profile) => ({
        ...profile,
        id: profile.resourceId,
        departmentLabel:
          getWorkforceDepartmentDisplayName(profile.companyDepartment?.name, currentLanguage) ||
          departmentLabelById.get(profile.companyDepartmentId) ||
          '',
        targetProfileTitle: profile.companyDepartmentTargetProfile?.title ?? '',
      })),
    [currentLanguage, departmentLabelById, filteredProfiles]
  );

  const showPagination = pagination && pagination.totalPages > 1 && !hasListFilters;
  const emptyMessage = hasListFilters ? String(t('list.filter.empty')) : String(t('list.empty'));

  if (!isWorkforceModuleActive) {
    return null;
  }

  return (
    <>
      {hasListFilters && (
        <WorkforceProfilesListFilterBanner
          matchedCount={matchedCount}
          filterCount={filterCount}
          isFetching={isFetchingFilteredPages}
          onClear={clearFilter}
        />
      )}

      <EmpionPaper
        sx={{
          border: `1px solid ${orangeColors[3]}`,
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        {isProfilesLoading && rowData.length === 0 ? (
          <Box sx={{py: 6}}>
            <Loading />
          </Box>
        ) : rowData.length === 0 ? (
          <EmptyStateCard message={emptyMessage} />
        ) : (
          <BasicTable
            columns={columns}
            Row={WorkforceProfileRow}
            rowData={rowData}
            hideLoadMoreFooter
            tableContainerSx={{overflowX: 'auto'}}
            tableSx={{tableLayout: 'auto'}}
            headerCellSx={{
              py: '10px',
              px: '16px',
              '& .MuiTypography-root': {
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              },
            }}
          />
        )}
      </EmpionPaper>

      {showPagination && (
        <Box sx={{mt: 2}}>
          <BasicTablePaginationFooter
            isLoading={isProfilesLoading}
            rowCount={rowData.length}
            pagination={{
              page: pagination.page,
              perPage: pagination.perPage,
              totalPages: pagination.totalPages,
              totalCount: pagination.totalCount,
              hasNext,
              hasPrevious,
              onNext: getNext,
              onPrevious: getPrevious,
              onPageChange: handlePageChange,
            }}
          />
        </Box>
      )}

      <CreateWorkforceProfileModal
        open={createModalOpen}
        companyId={companyId}
        departments={departments.data ?? []}
        onClose={onCreateModalClose}
        onCreated={refreshData}
      />
    </>
  );
};
